import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Checkbox } from '@/components/ui/checkbox';
import { Globe, EnvelopeSimple, Moon, Sun } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_da191abd-9276-45a7-bd48-1757c1f4e471/artifacts/p5urxzpf_diametor_logo_black__1_-removebg-preview.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_debt-cycle/artifacts/gzyrfqiy_diametor_logo_white-removebg-preview.png";

export default function LoginPage() {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  if (user) {
    if (user.profile_complete) {
      navigate('/dashboard', { replace: true });
    } else {
      navigate('/profile-setup', { replace: true });
    }
    return null;
  }

  const handleGoogleLogin = async () => {
    if (!termsAccepted) return;
    // PRODUCTION: ask the backend for the Google OAuth URL, then redirect.
    try {
      const res = await axios.get(`${API}/auth/google/login`);
      window.location.href = res.data.auth_url;
    } catch (e) {
      console.error('Failed to start Google login', e);
    }
  };

  const isDark = theme === 'dark';

  return (
    <div className={`min-h-screen flex flex-col ${isDark ? 'bg-zinc-950' : 'bg-white'}`} data-testid="login-page">
      <button
        onClick={toggleTheme}
        className={`fixed top-4 right-4 z-50 p-2.5 rounded-sm border transition-colors ${isDark ? 'border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-yellow-400' : 'border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-600 hover:text-yellow-500'}`}
        data-testid="login-theme-toggle"
      >
        {isDark ? <Sun size={18} weight="bold" /> : <Moon size={18} weight="bold" />}
      </button>

      <div className="flex-1 flex flex-col lg:flex-row">
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-zinc-950 items-center justify-center">
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/20 via-transparent to-transparent" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-400 rounded-full blur-[150px] opacity-20" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-yellow-400 rounded-full blur-[120px] opacity-15" />
          <div className="relative z-10 p-12 text-center">
            <img src={LOGO_DARK} alt="Diametor" className="w-80 mx-auto mb-8" data-testid="login-logo-hero" />
            <p className="font-ibm text-zinc-400 text-lg max-w-md mx-auto leading-relaxed">
              The AI-driven engine that makes invisible TOR cycles happen across millions simultaneously.
            </p>
          </div>
        </div>

        <div className={`flex-1 flex items-center justify-center p-6 lg:p-12 ${isDark ? 'bg-zinc-950' : 'bg-white'}`}>
          <div className="w-full max-w-sm">
            <div className="lg:hidden mb-10 flex justify-center">
              <img src={isDark ? LOGO_DARK : LOGO_LIGHT} alt="Diametor" className="w-48" data-testid="login-logo-mobile" />
            </div>

            <h1 className={`font-outfit text-3xl sm:text-4xl font-bold tracking-tight mb-2 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Welcome</h1>
            <p className={`font-ibm text-sm mb-10 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Sign in to manage your tor cycles & grow!</p>

            <div className="space-y-3 mb-8">
              <button
                onClick={handleGoogleLogin}
                disabled={!termsAccepted}
                className={`w-full flex items-center justify-center gap-3 h-12 font-ibm font-medium text-sm rounded-sm transition-all active:scale-[0.98] ${
                  termsAccepted
                    ? 'bg-yellow-400 text-zinc-950 hover:bg-yellow-500 cursor-pointer'
                    : `${isDark ? 'bg-zinc-800 text-zinc-500' : 'bg-zinc-200 text-zinc-400'} cursor-not-allowed`
                }`}
                data-testid="google-login-button"
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>

              <button
                disabled={true}
                className={`w-full flex items-center justify-center gap-3 h-12 font-ibm font-medium text-sm rounded-sm border transition-all ${isDark ? 'border-zinc-800 text-zinc-600' : 'border-zinc-200 text-zinc-400'} cursor-not-allowed opacity-50`}
                data-testid="apple-login-button"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Continue with Apple
                <span className={`font-ibm text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${isDark ? 'bg-zinc-800 text-zinc-500' : 'bg-zinc-100 text-zinc-400'}`}>Soon</span>
              </button>
            </div>

            <div className="flex items-start gap-3 mb-8">
              <Checkbox
                id="terms"
                checked={termsAccepted}
                onCheckedChange={setTermsAccepted}
                className={`mt-0.5 border-yellow-400 data-[state=checked]:bg-yellow-400 data-[state=checked]:text-zinc-950`}
                data-testid="terms-checkbox"
              />
              <label htmlFor="terms" className={`font-ibm text-xs leading-relaxed cursor-pointer ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                I accept the{' '}
                <a href="https://diametor.com/terms" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:text-yellow-300 underline underline-offset-2" data-testid="terms-link">
                  Terms and Conditions
                </a>
              </label>
            </div>
          </div>
        </div>
      </div>

      <footer className={`py-4 px-6 flex items-center justify-center gap-6 border-t ${isDark ? 'border-zinc-800 bg-zinc-950' : 'border-zinc-200 bg-white'}`} data-testid="login-footer">
        <a href="https://diametor.com" target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 font-ibm text-xs transition-colors ${isDark ? 'text-zinc-400 hover:text-yellow-400' : 'text-zinc-600 hover:text-yellow-500'}`} data-testid="website-link">
          <Globe size={16} weight="bold" /> diametor.com
        </a>
        <a href="mailto:support@diametor.com" className={`flex items-center gap-2 font-ibm text-xs transition-colors ${isDark ? 'text-zinc-400 hover:text-yellow-400' : 'text-zinc-600 hover:text-yellow-500'}`} data-testid="contact-link">
          <EnvelopeSimple size={16} weight="bold" /> support@diametor.com
        </a>
      </footer>
    </div>
  );
}
